import {
  crouch,
  E,
  flick,
  graffitiLine,
  hold,
  propsLine,
  ride,
  roll,
  tap,
  targetsLine,
  type DemoLine,
  type TagWall,
  type TargetRow,
} from "./script";

// depot's demo lines (#246), played in this order (`DEMO_LINES` in `demo.ts`).

/** The spot the targets line shoots from, on the high dock, and the row along its back wall (the map file's `targets`). */
const TARGETS: TargetRow = {
  range: { x: 31.5, z: -21.5 },
  row: [
    [28, -25.3],
    [29.8, -25.6],
    [31.6, -25.7],
    [33.4, -25.6],
    [35, -25.3],
  ],
};

// Built on call so the hill site drops them (#229; see `parkV2Lines`).
export const depotLines = (): DemoLine[] => [
  {
    id: "depot-drop",
    name: "Drop in, varial off the dock",
    mapId: "depot",
    // Off the high dock past the S: popped at its edge for the Dock Drop,
    // a varial kickflip on the way down, then braked to a stop on the floor.
    start: { x: 21.25, z: -23.5, yaw: 0, speed: 6 },
    segments: [hold("ollie", 0.5), roll(0.02), tap("flip", -1, -1), roll(1.55), { dur: 2.5, x: 0, y: -1 }],
    lands: ["Varial Kickflip", "Dock Drop"],
  },
  {
    id: "depot-rail",
    name: "The long rail: three grinds, heelflip off",
    mapId: "depot",
    // South down the long flat rail through C-O-M-B-O, switching grinds
    // (over two seconds on it), heelflip off near its end, then a carve
    // east into the open floor and a stop, short of the south wall.
    start: { x: -9, z: -17.5, yaw: 0, speed: 9 },
    segments: [
      crouch(0.25),
      roll(0.12),
      tap("grind"),
      ride(0.35),
      tap("grind", -1),
      ride(0.95),
      tap("grind", -0.7, -0.7),
      ride(0.95),
      crouch(0.1),
      roll(0.02),
      tap("flip", 1),
      roll(0.7),
      roll(1.2, -1),
      { dur: 3, x: 0, y: -1 },
    ],
    lands: ["50-50", "Boardslide", "Smith", "Heelflip"],
  },
  {
    id: "depot-tape",
    name: "Ollie off the dock's east end for the tape, BS 360 Melon",
    mapId: "depot",
    // From the targets' end of the high dock, popped at its edge: the air
    // off the 3 m dock goes up past the Secret Tape hanging over the floor
    // below. Rolling off without the pop drops short of it. A BS 360 Melon
    // on the way down, then braked to a stop.
    start: { x: 31, z: -23.5, yaw: 0, speed: 6 },
    segments: [hold("ollie", 0.5), roll(0.15), hold("grab", 0.9, 1, 0), roll(0.6), { dur: 2.5, x: 0, y: -1 }],
    lands: ["BS 360 Melon"],
  },
  {
    id: "depot-room",
    name: "50-50 the crate ledge",
    mapId: "depot",
    // East along the north wall past the E, an ollie onto the crate ledge's
    // end and a 50-50 along it, braked before the dock's side.
    start: { x: -20, z: -22.5, yaw: E, speed: 6 },
    segments: [roll(0.3), crouch(0.3), roll(0.12), tap("grind"), ride(0.5), roll(0.3), { dur: 2.5, x: 0, y: -1 }],
    lands: ["50-50"],
  },
  {
    id: "depot-manual",
    name: "Five flip tricks in a manual across the floor",
    mapId: "depot",
    // East across the floor below the dock in a manual, past the T, a flip
    // trick every 0.6 s, an ollie out of it and a stop.
    start: { x: -24, z: -16, yaw: E, speed: 8 },
    segments: [
      roll(0.05),
      ...flick([0, 1], [0, -1]),
      ride(0.158),
      tap("flip", 1, 1),
      ride(0.6),
      tap("flip", -1, 1),
      ride(0.6),
      tap("flip", 1, -1),
      ride(0.6),
      tap("flip", -1, 0),
      ride(0.6),
      tap("flip", 0, 1),
      ride(0.6),
      { dur: 0.1, x: 0, hold: ["ollie"] },
      roll(0.6),
      { dur: 1.5, x: 0, y: -1 },
    ],
    lands: ["Manual", "360 Flip", "Hardflip", "Varial Heelflip", "360 Shove-It", "Impossible"],
  },
];

/** The "Hit 5 targets" line (#137), played after every map's other lines. */
export const DEPOT_TARGETS: DemoLine = targetsLine("depot", TARGETS, "Paint the five targets on the high dock", 0);

/** The graffiti on the east wall by the mini halfpipe (the map file's `graffiti`), and where to stand to wash each tag. */
const GRAFFITI: TagWall = {
  face: E,
  stands: [
    [33.8, 15.5],
    [33.8, 19.5],
    [33.8, 23.5],
  ],
};

/** The "Wash off 3 graffiti tags" challenge (#151). */
export const DEPOT_GRAFFITI: DemoLine = graffitiLine("depot", GRAFFITI, "Power wash the graffiti off the east wall");

/** The washer knocking the cones and the can beside the long rail's north end about (#151, the map file's `props`). */
export const DEPOT_PROPS: DemoLine = propsLine(
  "depot-props",
  "depot",
  [-15, -15],
  0, // facing south (+z)
  [
    [-13.5, -10],
    [-16.5, -10],
  ],
  "Power wash a cone and a can across the floor",
);
